import { request } from './api.js';

export interface Simplification {
  simplification_id: string;
  project_id: string;
  node_id: string | null;
  title: string;
  title_ai: boolean; // Task B3 (#53A): "Titel aus KI-Vorschlag übernommen" — verschwindet bei manueller Änderung
  description_html: string | null;
  description_ai: boolean;
  sap_note: string | null;
  is_exception: boolean; // #33: bewusst ohne Testfall (Ausnahme-Zustand)
  linked_count: number; // #33: Anzahl verknuepfter Testfaelle (Abdeckungs-Indikator)
  created_at: string;
  updated_at: string;
}

export interface SimplificationCreateBody {
  title: string;
  title_ai?: boolean; // Task B3 (#53A): gesetzt vom "Übernehmen"-Panel, wenn unverändert aus der KI
  description_html?: string | null;
  description_ai?: boolean;
  sap_note?: string | null;
  node_id?: string | null;
  is_exception?: boolean;
}

// Review I2: die *_ai-Abzeichen sind im Update-Pfad server-eigen (der Server loescht sie beim
// Wertwechsel) — der Client kann sie nicht setzen. Der Server strippt sie ohnehin, der Typ macht
// den Versuch schon hier sichtbar.
export type SimplificationUpdateBody = Partial<
  Omit<SimplificationCreateBody, 'title_ai' | 'description_ai'>
>;

// Task B3 (#53A): Ergebnis von POST /simplifications:extract. `result === null` heißt "kein
// Vorschlag" — WARUM steht in `reason`, weil die drei Fehl-Ausgänge fachlich verschieden sind
// (Review I1) und das Panel sonst in zwei von drei Fällen etwas Unwahres melden müsste:
// 'unavailable' (kein Gateway/Laufzeitfehler), 'no_content' (die KI antwortete: nicht verwertbar),
// 'rejected' (Injection-Filter, der Text ging nie zur KI). Der eingefügte Note-Text selbst ist
// NICHT Teil der Antwort und wird vom Client nirgends zwischengespeichert.
export type SimplificationExtractReason = 'ok' | 'unavailable' | 'no_content' | 'rejected';

export interface SimplificationExtractResult {
  result: { title: string; description: string } | null;
  reason: SimplificationExtractReason;
}

// simplificationsRouter ist im Backend unter `/catalog` gemountet (siehe apps/api/src/index.ts).
const base = (p: string) => `/v1/projects/${p}/catalog`;

export const simplificationsApi = {
  // node_id filtert serverseitig (KAT-3-Muster) — siehe apps/api/src/routes/simplifications.ts.
  list: (p: string, nodeId?: string) =>
    request<Simplification[]>(
      `${base(p)}/simplifications${nodeId ? `?node_id=${encodeURIComponent(nodeId)}` : ''}`,
    ),
  create: (p: string, body: SimplificationCreateBody) =>
    request<Simplification>(`${base(p)}/simplifications`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (p: string, id: string, body: SimplificationUpdateBody) =>
    request<Simplification>(`${base(p)}/simplifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (p: string, id: string) =>
    request<void>(`${base(p)}/simplifications/${id}`, { method: 'DELETE' }),
  getLinks: (p: string, id: string) =>
    request<string[]>(`${base(p)}/simplifications/${id}/testcases`),
  // Task B3 (#53A): der eingefügte SAP-Note-Text geht EINMAL an diesen Aufruf und wird sonst
  // nirgends aufbewahrt (auch nicht im Client-State über den Aufruf hinaus).
  extract: (p: string, noteText: string) =>
    request<SimplificationExtractResult>(`${base(p)}/simplifications:extract`, {
      method: 'POST',
      body: JSON.stringify({ note_text: noteText }),
    }),
  setLinks: (p: string, id: string, testCaseIds: string[]) =>
    request<{ ok: true }>(`${base(p)}/simplifications/${id}/testcases`, {
      method: 'PUT',
      body: JSON.stringify({ test_case_ids: testCaseIds }),
    }),
};
